"use client"

import * as React from "react"
import { CheckCircleIcon, CircleIcon, EyeIcon, EyeSlashIcon } from "@phosphor-icons/react"
import { cn } from "cn"

import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from "./input-group"

function PasswordInput({
  className,
  showLabel = "Show password",
  hideLabel = "Hide password",
  disabled,
  ...props
}: Omit<React.ComponentProps<"input">, "type"> & { showLabel?: string; hideLabel?: string }) {
  const [visible, setVisible] = React.useState(false)
  return (
    <InputGroup data-slot="password-input" data-disabled={disabled || undefined} className={className}>
      <InputGroupInput type={visible ? "text" : "password"} disabled={disabled} {...props} />
      <InputGroupAddon align="inline-end">
        <InputGroupButton
          size="icon-xs"
          aria-label={visible ? hideLabel : showLabel}
          aria-pressed={visible}
          disabled={disabled}
          onClick={() => setVisible((current) => !current)}
        >
          {visible ? <EyeSlashIcon /> : <EyeIcon />}
        </InputGroupButton>
      </InputGroupAddon>
    </InputGroup>
  )
}

type PasswordRequirement = { id: string; label: string; met: boolean }

function PasswordRequirements({
  requirements,
  metLabel = "met",
  unmetLabel = "not met",
  className,
  ...props
}: React.ComponentProps<"ul"> & { requirements: PasswordRequirement[]; metLabel?: string; unmetLabel?: string }) {
  return (
    <ul data-slot="password-requirements" className={cn("flex flex-col gap-1.5", className)} {...props}>
      {requirements.map((requirement) => (
        <li
          key={requirement.id}
          data-met={requirement.met || undefined}
          className="typo-body-sm flex items-center gap-2 text-muted-foreground transition-colors data-met:text-success"
        >
          {requirement.met ? (
            <CheckCircleIcon weight="fill" aria-hidden="true" className="size-4 shrink-0" />
          ) : (
            <CircleIcon aria-hidden="true" className="size-4 shrink-0 text-greyscale-300 dark:text-greyscale-600" />
          )}
          <span>{requirement.label}</span>
          <span className="sr-only">({requirement.met ? metLabel : unmetLabel})</span>
        </li>
      ))}
    </ul>
  )
}

export { PasswordInput, PasswordRequirements, type PasswordRequirement }
